import React, { useEffect, useMemo, useState } from 'react';
import QuickSearchModal from './QuickSearchModal';
import instance from '../../API/api';

type Mode = 'company' | 'driver' | 'dashUser' | 'adminUser' | null;

type ValueType = string | number;
type OptionType = { value: ValueType; label: React.ReactNode };

export default function GlobalQuickSearch() {
  const [mode, setMode] = useState<Mode>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey) return; // Alt Windows / Option Mac
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;

      if (e.code === 'KeyC') {
        e.preventDefault();
        setMode('company');
      }
      if (e.code === 'KeyD') {
        e.preventDefault();
        setMode('driver');
      }
      if (e.code === 'KeyU') {
        e.preventDefault();
        setMode('dashUser');
      }
      if (e.code === 'KeyA') {
        e.preventDefault();
        setMode('adminUser');
      }
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true } as any);
  }, []);

  const config = useMemo(() => {
    if (mode === 'company') {
      return {
        title: 'Company search',
        placeholder: 'Search company...',
        shortcutLabel: 'Alt / Option + C',
        fetchOptions: async (q: string) => {
          const res = await instance.get('companies/list', {
            params: { searchName: q },
          });

          const list = res?.data?.data ?? res?.data ?? [];

          return list.map((c: any) => ({
            value: c.id,
            label: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.name || '-'}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Company</div>
                </div>

                <div
                  style={{
                    fontSize: 12,
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: 'rgba(0,0,0,0.05)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ID: {c.id}
                </div>
              </div>
            ),
          }));
        },
        to: (id: ValueType): string => `/companies/${id}`,
      };
    }

    if (mode === 'driver') {
      return {
        title: 'Driver search',
        placeholder: 'Search driver...',
        shortcutLabel: 'Alt / Option + D',
        fetchOptions: async (q: string) => {
          const res = await instance.get('users/searching-list', {
            params: { searchName: q, role: 'driver' },
          });

          const list = res?.data?.data ?? [];

          return list.map((d: any) => {
            const fullName = [d.first_name, d.second_name].filter(Boolean).join(' ') || '-';
            const companyName = d.company?.name || '-';
            const username = d.username || '-';

            return {
              value: d.id,
              label: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fullName}
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.75,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      @{username} • {companyName}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'rgba(0,0,0,0.05)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ID: {d.id}
                    </div>

                    {d.publicId ? (
                      <div
                        style={{
                          fontSize: 11,
                          opacity: 0.65,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Public ID: {d.publicId}
                      </div>
                    ) : null}
                  </div>
                </div>
              ),
            };
          });
        },
        to: (id: ValueType): string => `/user/users/${id}`,
      };
    }

    if (mode === 'dashUser') {
      return {
        title: 'Dashboard user search',
        placeholder: 'Search dashboard user...',
        shortcutLabel: 'Alt / Option + U',

        fetchOptions: async (q: string) => {
          const res = await instance.get('users/searching-list', {
            params: {
              searchName: q,
              role: 'dispatcher',
            },
          });

          const list = res?.data?.data ?? [];

          return list.map((u: any) => {
            const fullName = [u.first_name, u.second_name].filter(Boolean).join(' ') || '-';

            const role = u.role || '-';

            return {
              value: u.id,
              label: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fullName}
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.75,
                      }}
                    >
                      Role: {role}
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: 'rgba(0,0,0,0.05)',
                    }}
                  >
                    ID: {u.id}
                  </div>
                </div>
              ),
            };
          });
        },

        to: (id: ValueType): string => `/user/dispatcher/${id}`,
      };
    }

    if (mode === 'adminUser') {
      return {
        title: 'Admin user search',
        placeholder: 'Search admin user...',
        shortcutLabel: 'Alt / Option + A',
        fetchOptions: async (q: string): Promise<OptionType[]> => {
          const res = await instance.get('admins/get-all', {
            params: { searchName: q },
          });

          const list = res?.data?.data ?? res?.data ?? [];

          return list.map((u: any) => {
            const fullName = [u.first_name, u.second_name].filter(Boolean).join(' ') || '-';

            const username = u.username || '-';
            const role = u.role || '-';
            const isActive = Boolean(u.is_active);

            return {
              value: u.id,
              label: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fullName}
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.75,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      @{username} • {role}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'rgba(0,0,0,0.05)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ID: {u.id}
                    </div>

                    <div
                      style={{
                        fontSize: 11,
                        color: isActive ? '#389e0d' : '#cf1322',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              ),
            };
          });
        },
        to: (id: ValueType): string => `/user/admins/${id}`,
      };
    }

    return null;
  }, [mode]);

  return (
    <>
      {config && (
        <QuickSearchModal
          open={!!mode}
          onClose={() => setMode(null)}
          title={config.title}
          placeholder={config.placeholder}
          fetchOptions={config.fetchOptions}
          shortcutLabel={config.shortcutLabel}
          to={config.to}
        />
      )}
    </>
  );
}
